import React, { useEffect, useId, useMemo, useState } from "react";
import AdaptiveQueryResultChart from "./charts/AdaptiveQueryResultChart";
import { formatFieldLabel } from "./utils/visualizationData";
import { buildRankingPresentationModel } from "./utils/rankingVisualization";
import classes from "./GenericQueryResultVisualization.module.css";

const EmptyState = ({ children }) => (
  <div className={classes.empty_state} role="alert" aria-live="polite">
    {children}
  </div>
);

const MetricPillSelector = ({ activeMetric, groupName, metrics, onChange }) => {
  if (metrics.length <= 1) return null;
  return (
    <fieldset className={classes.metric_selector} role="radiogroup">
      <legend>Indicator</legend>
      <div className={classes.metric_pills}>
        {metrics.map(({ field, label }) => (
          <label key={field} className={classes.metric_pill}>
            <input
              type="radio"
              name={groupName}
              value={field}
              checked={field === activeMetric}
              onChange={() => onChange(field)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export default function RankingQueryResultVisualization({ response, labels = {} }) {
  const rows = useMemo(
    () => (Array.isArray(response?.query_result) ? response.query_result : []),
    [response?.query_result],
  );
  const metricGroupName = useId();
  const [selectedParent, setSelectedParent] = useState(null);
  const [activeMetricField, setActiveMetricField] = useState(null);
  const model = useMemo(
    () =>
      buildRankingPresentationModel({
        response,
        rows,
        labels,
        selectedParent,
        activeMetric: activeMetricField,
      }),
    [activeMetricField, labels, response, rows, selectedParent],
  );
  const resolvedParentKey = model.selectedParent?.key || null;
  const resolvedMetricField = model.rankingMetric || null;

  useEffect(() => {
    if (selectedParent !== resolvedParentKey) setSelectedParent(resolvedParentKey);
  }, [resolvedParentKey, selectedParent]);

  useEffect(() => {
    if (activeMetricField !== resolvedMetricField) setActiveMetricField(resolvedMetricField);
  }, [activeMetricField, resolvedMetricField]);

  const parentOptions = model.parentOptions || [];
  const contextValues = model.contextValues || [];
  const availableMetrics = model.availableMetrics || [];
  const hasParentSelector = model.mode === "grouped" && parentOptions.length > 1;
  const hasMetricSelector = model.status === "ready" && availableMetrics.length > 1;
  const selectedContext = contextValues.map(({ label, value }) => ({ label, value }));

  return (
    <section
      className={classes.visualization}
      aria-label="Ranking query result visualization"
      data-visualization-policy="ranking"
      data-ranking-mode={model.mode}
    >
      {hasParentSelector && (
        <div className={classes.controls} data-testid="visualization-controls">
          <label className={classes.control_label}>
            <span>{labels[model.parentField] || formatFieldLabel(model.parentField)}</span>
            <select
              aria-label={labels[model.parentField] || formatFieldLabel(model.parentField)}
              value={model.selectedParent?.key || ""}
              onChange={(event) => setSelectedParent(event.target.value)}
            >
              {parentOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {contextValues.length > 0 && (
        <div className={classes.context_values} aria-label="Selected data context">
          {contextValues.map(({ field, label, value }) => (
            <span key={field}>
              <strong>{label}</strong>
              {String(value)}
            </span>
          ))}
        </div>
      )}

      {hasMetricSelector && (
        <MetricPillSelector
          activeMetric={model.rankingMetric}
          groupName={metricGroupName}
          metrics={availableMetrics}
          onChange={setActiveMetricField}
        />
      )}

      {model.status !== "ready" ? (
        <EmptyState>{model.warning}</EmptyState>
      ) : (
        <AdaptiveQueryResultChart
          metricLabel={labels[model.rankingMetric] || formatFieldLabel(model.rankingMetric)}
          mode="categorical"
          model={model.chartModel}
          ranking={{
            title: model.title,
            mode: model.mode,
            rankedEntityField: model.rankedEntityField,
            rankingMetric: model.rankingMetric,
          }}
          selectedContext={selectedContext}
          viewByLabel={labels[model.rankedEntityField] || formatFieldLabel(model.rankedEntityField)}
        />
      )}
    </section>
  );
}

